import { GRID_PAR, type GridMode } from './types';

export type ShareCell = 'par' | 'bogey' | 'bad' | 'empty';

/** Spoiler-free emoji share card. Classic mode is a 9-hole golf scorecard:
 *  each cell is a hole with par 1, colored by how many placements it took —
 *  🟩 one (par), 🟨 two (bogey), 🟥 three+ or a still-invalid cell; cell
 *  strokes sum to the move count, so the grid IS the golf score, decomposed:
 *    THE MOTORING GRID #012
 *    🟩🟩🟨
 *    🟩🟩🟩
 *    🟨🟥🟩
 *    ⛳ +4 — 13 moves · 1 fault
 *  A flawless win reads "⛳ Par — clean sweep" and is all-green — earned, not
 *  automatic. One-Shot mode tags the header "· ONE-SHOT" and colors by
 *  correctness (🟩 right / 🟥 wrong), with its own 🎯 emblem line — a clean
 *  board reads "🎯 Flawless — 9/9 blind", otherwise "🎯 6/9 · 3 faults".
 *  (Classic resignations show ⬜ for empty holes and read "n/9".)
 *  Streak line when ≥2; dated https link resolves via /play day-routing. */
export function shareGrid(
  puzzleNumber: number,
  mode: GridMode,
  cells: ShareCell[], // 9 entries, row-major
  solved: boolean,
  score: number, // server-derived — the n in "n/9" for unsolved boards
  moves: number,
  faults: number,
  streak: number | null,
  date: string,
  filterLabel?: string | null, // monthly special: "· ALL JAPANESE" badge
): string {
  // Each mode owns a scoreline emblem that doubles as its signature: ⛳ golf
  // for Classic, 🎯 for One-Shot. The header spells out the mode so the marker
  // is never ambiguous (the old bare 🔒 read as "locked/hard/private").
  const modeTag = mode === 'hard' ? ' · ONE-SHOT' : '';
  const badge = filterLabel ? ` · ALL ${filterLabel.toUpperCase()}` : '';
  const header = `THE MOTORING GRID #${String(puzzleNumber).padStart(3, '0')}${badge}${modeTag}`;
  const glyph: Record<ShareCell, string> = { par: '🟩', bogey: '🟨', bad: '🟥', empty: '⬜' };
  const rows = [0, 1, 2].map((r) =>
    cells
      .slice(r * 3, r * 3 + 3)
      .map((c) => glyph[c])
      .join(''),
  );
  const faultsBit = `${faults} fault${faults === 1 ? '' : 's'}`;
  const line =
    mode === 'hard'
      ? solved
        ? `🎯 Flawless — ${GRID_PAR}/${GRID_PAR} blind`
        : `🎯 ${score}/${GRID_PAR} · ${faultsBit}`
      : solved
        ? moves === GRID_PAR
          ? '⛳ Par — clean sweep'
          : `⛳ +${moves - GRID_PAR} — ${moves} moves · ${faultsBit}`
        : `${score}/${GRID_PAR} · ${faultsBit}`;
  const streakLine = streak && streak >= 2 ? [`🔥 ${streak}-day streak`] : [];
  return [header, ...rows, line, ...streakLine, `https://namethecar.com/${date}`].join('\n');
}
